import type { Maybe } from '@adornis/base/utilTypes.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { html, nothing } from 'lit';

interface FormGridEntryProps {
  /**
   * The left label to show
   */
  label: string;
  /**
   * Override column span (default: 1)
   */
  labelSpan?: 1 | 2;
  /**
   * Label margin top
   * Sometimes on large rendarable we want a custom margin top so the label gets not centered to the content,
   * but it more like a heading. With this you can control the margin to the labels container at the top.
   * Example:
   *        ------     label: ------
   * label: ------  ->        ------
   *        ------            ------
   */
  labelTopMargin?: string;

  /**
   * Optional subtext rendered below the label in a smaller, muted style.
   */
  labelSubtext?: string | Renderable;

  /**
   * Label additions. Rendered with the label horizontally next to it.
   */
  labelAddition?: Renderable;

  /**
   * The renderable in the form. The content of the form entry. Most likely an input
   */
  render: Renderable | typeof nothing;
  /**
   * Override column span (default: 1)
   * As there is a maximum of two columns, this should be 1 or 2
   */
  renderSpan?: 1 | 2;

  history?: Array<Maybe<string>>;
}

export const FormGridEntry = stateful((props: FormGridEntryProps) => {
  /**
   * Ensure grid spans of label and renderable and check validity
   */
  props.labelSpan ??= 1;
  props.renderSpan ??= 1;
  if (props.labelSpan + props.renderSpan !== 2) {
    throw new Error('labelSpan + renderSpan must equal 2');
  }

  /**
   * Parse History array to string
   */
  const historyRender = props.history?.length
    ? props.history.map((value, index) => html`${index > 0 ? html` → ` : nothing}<b>${value ?? '---'}</b>`)
    : null;

  return html`
    <!-- Label -->
    <div
      class="flex flex-row justify-between ${props.labelTopMargin ? props.labelTopMargin : 'items-center'}"
      style="grid-column: span ${props.labelSpan}"
    >
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-semibold text-base-content/70">${props.label}</span>
        ${props.labelSubtext ? html`<p class="text-sm text-base-content/70">${props.labelSubtext}</p>` : nothing}
      </div>
      ${props.labelAddition ?? nothing}
    </div>

    <!-- Renderable -->
    <div
      class="p-1 ${historyRender ? 'martinus-history-marker tooltip' : ''}"
      style="grid-column: span ${props.renderSpan}"
      ${hoverTooltip()}
    >
      ${historyRender ? html`<div class="tooltip-content">${historyRender}</div>` : nothing} ${props.render}
    </div>
  `;
});
